import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ArrowLeft,
  Archive,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  Lock,
  Pencil,
  Play,
  Plus,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import type { ExamCycleDetailResponse } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { SelectField } from '../components/ui/Field';
import { Skeleton } from '../components/ui/Skeleton';
import {
  CycleDrawer,
  CycleStatusChip,
  CycleTypeChip,
  MarkGrid,
  SubjectDrawer,
  canEditCycleMetadata,
  canEditMarks,
  canEditSubjects,
  canTransition,
  type CycleDrawerMode,
  type CycleResults,
  type ExamCycleDetail,
  type ExamCycleSubject,
  type MarksGrid,
  type SubjectDrawerMode,
} from '../components/exams';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { GatedSection } from '../components/shared/GatedSection';
import { RelationPanel } from '../components/shared/RelationPanel';

export const Route = createFileRoute('/_authed/academics/exams/$cycleId')({
  component: ExamCycleDetailPage,
});

type Tab = 'subjects' | 'marks' | 'visibility' | 'audience' | 'results' | 'activity';

const TAB_OPTIONS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'subjects', label: 'Subjects' },
  { value: 'marks', label: 'Mark entry' },
  { value: 'visibility', label: 'Visibility' },
  { value: 'audience', label: 'Audience' },
  { value: 'results', label: 'Results' },
  { value: 'activity', label: 'Activity' },
];

// ---------------------------------------------------------------------------
// Wire shapes for the parallel-built endpoints (backend agent producing).
// Validation has no schema yet, so we mirror the salaries precedent and
// declare a local extension with optional fields — header degrades gracefully
// before the backend lands the LEFT JOIN enrichment.
// ---------------------------------------------------------------------------

interface ExamCycleDetailEnriched extends ExamCycleDetail {
  createdByName?: string | null;
  updatedByName?: string | null;
}

interface AudienceStudentRow {
  studentId: string;
  studentName: string;
  admissionNumber: string;
  recordedSubjects: number;
  missingSubjects: number;
  isVisible: boolean;
  hasVisibilityOverride: boolean;
}

interface AudienceBatchGroup {
  batchId: string;
  batchName: string;
  courseName: string | null;
  students: ReadonlyArray<AudienceStudentRow>;
}

interface AudienceResponse {
  totalStudents: number;
  groupedByBatch: ReadonlyArray<AudienceBatchGroup>;
}

function ExamCycleDetailPage() {
  const { cycleId } = Route.useParams();
  const queryClient = useQueryClient();
  const hasRole = useAuthStore((s) => s.hasRole);
  const canManage = hasRole('super_admin', 'academic_admin');
  const canWriteMarks = hasRole('super_admin', 'academic_admin', 'teacher');

  const [tab, setTab] = useState<Tab>('subjects');
  const [editCycle, setEditCycle] = useState<CycleDrawerMode | null>(null);

  const cycleQuery = useQuery({
    queryKey: ['exams', 'cycle', cycleId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ExamCycleDetailResponse>(
        `/api/exams/cycles/${encodeURIComponent(cycleId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // Single lifted audience query — feeds the new KPIs, the Audience tab, and
  // enriches the Visibility tab's per-student override resolution. We keep
  // it enabled regardless of the active tab so the KPI strip never flickers
  // when the operator switches tabs.
  const audienceQuery = useQuery({
    queryKey: ['exams', 'cycle', cycleId, 'audience'] as const,
    queryFn: ({ signal }) =>
      apiRequest<AudienceResponse>(
        `/api/exams/cycles/${encodeURIComponent(cycleId)}/audience`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const cycle = (cycleQuery.data ?? null) as ExamCycleDetailEnriched | null;
  const subjects = cycle?.subjects ?? [];

  const invalidateCycle = () => {
    void queryClient.invalidateQueries({ queryKey: ['exams', 'cycle', cycleId] });
    void queryClient.invalidateQueries({ queryKey: ['exams', 'cycles'] });
  };

  // Per-mutation refresh on visibility changes also re-pulls audience so the
  // override chip and the resolved Visible/Hidden state stay in sync with
  // what the server now reports.
  const invalidateAfterVisibilityChange = () => {
    invalidateCycle();
    void queryClient.invalidateQueries({
      queryKey: ['exams', 'cycle', cycleId, 'audience'],
    });
  };

  return (
    <ListShell
      title={cycle?.name ?? 'Exam cycle'}
      description={cycle ? `${cycle.academicYear}${cycle.term ? ` · ${cycle.term}` : ''}` : undefined}
    >
      <nav aria-label="Breadcrumb" className="-mt-2 mb-4">
        <Link
          to="/academics/exams"
          className={clsx(
            'inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)]',
            'transition-colors hover:text-[color:var(--color-text)]',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
          )}
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to exam cycles
        </Link>
      </nav>

      <GatedSection
        error={cycleQuery.error}
        title="Exam cycle"
        onRetry={() => cycleQuery.refetch()}
      >
        {cycleQuery.isLoading || !cycle ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-12 w-72" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <CycleSummary
              cycle={cycle}
              canManage={canManage}
              onEdit={() => setEditCycle({ kind: 'edit', cycle })}
              onTransitioned={invalidateCycle}
              audience={audienceQuery.data ?? null}
              audienceLoading={audienceQuery.isLoading && !audienceQuery.data}
            />

            <div
              role="tablist"
              aria-label="Cycle sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TAB_OPTIONS.map((opt) => {
                const active = tab === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(opt.value)}
                    className={clsx(
                      'rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {tab === 'subjects' ? (
              <SubjectsTab
                cycle={cycle}
                subjects={subjects}
                canManage={canManage}
                onChanged={invalidateCycle}
              />
            ) : null}
            {tab === 'marks' ? (
              <MarksTab
                cycle={cycle}
                subjects={subjects}
                canWrite={canWriteMarks}
                onSaved={invalidateCycle}
              />
            ) : null}
            {tab === 'visibility' ? (
              <VisibilityTab
                cycle={cycle}
                canManage={canManage}
                onChanged={invalidateAfterVisibilityChange}
                audience={audienceQuery.data ?? null}
              />
            ) : null}
            {tab === 'audience' ? (
              <AudienceTab
                audience={audienceQuery.data ?? null}
                loading={audienceQuery.isLoading && !audienceQuery.data}
                error={
                  audienceQuery.isError && !audienceQuery.data
                    ? audienceQuery.error
                    : null
                }
                onRetry={() => {
                  void audienceQuery.refetch();
                }}
              />
            ) : null}
            {tab === 'results' ? <ResultsTab cycleId={cycle.id} /> : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="exam_cycle"
                targetId={cycle.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <CycleDrawer
        mode={editCycle}
        onClose={() => setEditCycle(null)}
        onSaved={() => {
          setEditCycle(null);
          invalidateCycle();
        }}
      />
    </ListShell>
  );
}

// ---------- Summary header ----------

interface CycleSummaryProps {
  cycle: ExamCycleDetailEnriched;
  canManage: boolean;
  onEdit: () => void;
  onTransitioned: () => void;
  audience: AudienceResponse | null;
  audienceLoading: boolean;
}

function CycleSummary({
  cycle,
  canManage,
  onEdit,
  onTransitioned,
  audience,
  audienceLoading,
}: CycleSummaryProps) {
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const transitionMutation = useMutation({
    mutationFn: async (action: 'activate' | 'lock' | 'archive') => {
      return apiRequest<{ ok: true; status: string }>(
        `/api/exams/cycles/${encodeURIComponent(cycle.id)}/${action}`,
        { method: 'POST' },
      );
    },
    onSuccess: (res, action) => {
      const label =
        action === 'activate' ? 'activated' : action === 'lock' ? 'locked' : 'archived';
      toast(`Cycle ${label}.`, { variant: 'success' });
      onTransitioned();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError ? err.message : 'Could not change status.';
      toast(message, { variant: 'error' });
    },
  });

  const runTransition = async (
    action: 'activate' | 'lock' | 'archive',
    title: string,
    description: string,
  ) => {
    const ok = await confirm({
      title,
      description,
      confirmLabel: action.charAt(0).toUpperCase() + action.slice(1),
      variant: action === 'archive' ? 'danger' : 'default',
    });
    if (!ok) return;
    transitionMutation.mutate(action);
  };

  return (
    <section className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-semibold truncate">
              {cycle.name}
            </h2>
            <CycleStatusChip status={cycle.status} />
            <CycleTypeChip type={cycle.type} />
          </div>
          <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
            {cycle.academicYear}
            {cycle.term ? ` · ${cycle.term}` : ''} ·{' '}
            {formatDate(`${cycle.startDate}T00:00:00Z`)} –{' '}
            {formatDate(`${cycle.endDate}T00:00:00Z`)}
          </p>
          {cycle.createdByName || cycle.updatedByName ? (
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[color:var(--color-text-muted)]">
              {cycle.createdByName ? (
                <span>Created by {cycle.createdByName}</span>
              ) : null}
              {cycle.createdByName && cycle.updatedByName ? (
                <span aria-hidden="true">·</span>
              ) : null}
              {cycle.updatedByName ? (
                <span>Updated by {cycle.updatedByName}</span>
              ) : null}
            </p>
          ) : null}
          {cycle.description ? (
            <p className="mt-2 text-sm text-[color:var(--color-text)]">
              {cycle.description}
            </p>
          ) : null}
        </div>
        {canManage ? (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil size={14} aria-hidden="true" />}
              onClick={onEdit}
              disabled={!canEditCycleMetadata(cycle.status)}
              title={
                !canEditCycleMetadata(cycle.status)
                  ? 'Archived cycles cannot be edited.'
                  : undefined
              }
            >
              Edit cycle
            </Button>
            {canTransition(cycle.status, 'activate') ? (
              <Button
                size="sm"
                icon={<Play size={14} aria-hidden="true" />}
                onClick={() =>
                  runTransition(
                    'activate',
                    'Activate this cycle?',
                    'Once active, staff can record marks against its subjects.',
                  )
                }
                loading={transitionMutation.isPending}
              >
                Activate
              </Button>
            ) : null}
            {canTransition(cycle.status, 'lock') ? (
              <Button
                size="sm"
                variant="secondary"
                icon={<Lock size={14} aria-hidden="true" />}
                onClick={() =>
                  runTransition(
                    'lock',
                    'Lock this cycle?',
                    'Marks will become read-only. You can still toggle visibility for students and parents.',
                  )
                }
                loading={transitionMutation.isPending}
              >
                Lock marks
              </Button>
            ) : null}
            {canTransition(cycle.status, 'archive') ? (
              <Button
                size="sm"
                variant="ghost"
                icon={<Archive size={14} aria-hidden="true" />}
                onClick={() =>
                  runTransition(
                    'archive',
                    'Archive this cycle?',
                    'Archived cycles cannot be edited and disappear from the default list filter.',
                  )
                }
                loading={transitionMutation.isPending}
                className="text-[color:var(--color-text-muted)]"
              >
                Archive
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <dl className="mt-5 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Subjects" value={String(cycle.subjects.length)} />
        <Stat
          label="Marks recorded"
          value={String(cycle.marksRecordedCount)}
        />
        <Stat
          label="Expected entries"
          value={
            audienceLoading ? (
              <Skeleton className="h-6 w-12" />
            ) : audience ? (
              String(audience.totalStudents * cycle.subjects.length)
            ) : (
              '—'
            )
          }
          sub={
            audience
              ? `${audience.totalStudents} students × ${cycle.subjects.length} subjects`
              : null
          }
        />
        <Stat
          label="Completion"
          value={
            audienceLoading ? (
              <Skeleton className="h-6 w-14" />
            ) : (
              computeCompletionLabel(cycle, audience)
            )
          }
        />
        <Stat
          label="Visible by default"
          value={cycle.visibility.cycleDefault ? 'Yes' : 'No'}
        />
        <Stat
          label="Student overrides"
          value={String(cycle.visibility.studentOverridesCount)}
        />
      </dl>
    </section>
  );
}

function computeCompletionLabel(
  cycle: ExamCycleDetail,
  audience: AudienceResponse | null,
): string {
  if (!audience) return '—';
  const denom = audience.totalStudents * cycle.subjects.length;
  if (denom <= 0) return '—';
  const pct = Math.round((cycle.marksRecordedCount / denom) * 100);
  // Clamp to 0..100 — server may report extra entries (e.g. withdrawn
  // students whose marks linger) and we don't want >100% in the UI.
  const clamped = Math.max(0, Math.min(100, pct));
  return `${clamped}%`;
}

interface StatProps {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}

function Stat({ label, value, sub }: StatProps): JSX.Element {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="mt-0.5 font-display text-xl font-bold text-[color:var(--color-text)]">
        {value}
      </dd>
      {sub ? (
        <p className="mt-0.5 text-[11px] text-[color:var(--color-text-muted)]">
          {sub}
        </p>
      ) : null}
    </div>
  );
}

// ---------- Subjects tab ----------

interface SubjectsTabProps {
  cycle: ExamCycleDetail;
  subjects: ReadonlyArray<ExamCycleSubject>;
  canManage: boolean;
  onChanged: () => void;
}

function SubjectsTab({ cycle, subjects, canManage, onChanged }: SubjectsTabProps) {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [drawer, setDrawer] = useState<SubjectDrawerMode | null>(null);
  const editable = canEditSubjects(cycle.status);
  const cycleLockReason =
    cycle.status === 'marks_locked'
      ? 'Cycle is locked — subjects cannot be changed.'
      : cycle.status === 'archived'
        ? 'Cycle is archived — subjects cannot be changed.'
        : null;

  const deleteMutation = useMutation({
    mutationFn: (subjectId: string) =>
      apiRequest<{ ok: true }>(
        `/api/exams/subjects/${encodeURIComponent(subjectId)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      toast('Subject removed.', { variant: 'success' });
      onChanged();
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not delete subject.',
        { variant: 'error' },
      );
    },
  });

  const handleDelete = async (subject: ExamCycleSubject) => {
    const ok = await confirm({
      title: `Delete ${subject.subjectName}?`,
      description:
        'This removes the subject and any marks recorded against it. This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(subject.id);
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold">Subjects</h3>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Define the subjects assessed during this cycle. Max-marks and pass-marks set the
            grading scale.
          </p>
        </div>
        {canManage ? (
          <Button
            size="sm"
            icon={<Plus size={14} aria-hidden="true" />}
            onClick={() => setDrawer({ kind: 'create', cycleId: cycle.id })}
            disabled={!editable}
            title={cycleLockReason ?? undefined}
          >
            Add subject
          </Button>
        ) : null}
      </div>

      {subjects.length === 0 ? (
        <EmptyState
          title="No subjects yet"
          description={
            canManage
              ? 'Add subjects to define what will be graded in this cycle.'
              : 'No subjects have been added to this cycle yet.'
          }
          action={
            canManage ? (
              <Button
                size="sm"
                icon={<Plus size={14} aria-hidden="true" />}
                onClick={() => setDrawer({ kind: 'create', cycleId: cycle.id })}
                disabled={!editable}
              >
                Add subject
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)]">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                >
                  Order
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                >
                  Subject
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                >
                  Max
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                >
                  Pass
                </th>
                <th
                  scope="col"
                  className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                >
                  Recorded
                </th>
                {canManage ? <th scope="col" className="px-4 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {subjects.map((subject) => (
                <tr
                  key={subject.id}
                  className="border-t border-[color:var(--color-border)]"
                >
                  <td className="px-4 py-2 align-middle tabular-nums text-xs text-[color:var(--color-text-muted)]">
                    {subject.orderIndex}
                  </td>
                  <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                    {subject.subjectName}
                  </td>
                  <td className="px-4 py-2 align-middle tabular-nums">
                    {subject.maxMarks}
                  </td>
                  <td className="px-4 py-2 align-middle tabular-nums">
                    {subject.passMarks}
                  </td>
                  <td className="px-4 py-2 align-middle tabular-nums text-[color:var(--color-text-muted)]">
                    {subject.marksRecordedCount}
                  </td>
                  {canManage ? (
                    <td className="px-4 py-2 align-middle text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Pencil size={14} aria-hidden="true" />}
                          onClick={() =>
                            setDrawer({ kind: 'edit', cycleId: cycle.id, subject })
                          }
                          disabled={!editable}
                          title={cycleLockReason ?? undefined}
                          aria-label={`Edit ${subject.subjectName}`}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<Trash2 size={14} aria-hidden="true" />}
                          onClick={() => handleDelete(subject)}
                          disabled={!editable || deleteMutation.isPending}
                          title={cycleLockReason ?? undefined}
                          className="text-danger hover:bg-[color:var(--color-danger)]/10"
                          aria-label={`Delete ${subject.subjectName}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubjectDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          onChanged();
        }}
      />
    </section>
  );
}

// ---------- Mark entry tab ----------

interface MarksTabProps {
  cycle: ExamCycleDetail;
  subjects: ReadonlyArray<ExamCycleSubject>;
  canWrite: boolean;
  onSaved: () => void;
}

function MarksTab({ cycle, subjects, canWrite, onSaved }: MarksTabProps) {
  const [subjectId, setSubjectId] = useState<string>(subjects[0]?.id ?? '');

  // Keep selection in sync if subject list changes (e.g. one was deleted).
  const validSubjectId = useMemo(() => {
    if (subjects.some((s) => s.id === subjectId)) return subjectId;
    return subjects[0]?.id ?? '';
  }, [subjects, subjectId]);

  const gridQuery = useQuery({
    queryKey: ['exams', 'marks-grid', cycle.id, validSubjectId] as const,
    enabled: Boolean(validSubjectId),
    queryFn: ({ signal }) =>
      apiRequest<MarksGrid>(
        `/api/exams/cycles/${encodeURIComponent(
          cycle.id,
        )}/subjects/${encodeURIComponent(validSubjectId)}/marks`,
        { signal },
      ),
  });

  if (subjects.length === 0) {
    return (
      <EmptyState
        title="Add a subject first"
        description="Marks can be recorded once at least one subject exists for this cycle."
      />
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px]">
          <SelectField
            label="Subject"
            value={validSubjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            options={subjects.map((s) => ({
              value: s.id,
              label: `${s.subjectName} (max ${s.maxMarks})`,
            }))}
          />
        </div>
        {!canEditMarks(cycle.status) ? (
          <p className="text-xs text-[color:var(--color-text-muted)]">
            {cycle.status === 'draft'
              ? 'Activate the cycle to record marks.'
              : cycle.status === 'marks_locked'
                ? 'Cycle is locked — marks are read-only.'
                : 'Cycle is archived — marks are read-only.'}
          </p>
        ) : null}
      </div>

      <GatedSection
        error={gridQuery.error}
        title="Mark grid"
        onRetry={() => gridQuery.refetch()}
      >
        {gridQuery.isLoading || !gridQuery.data ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <MarkGrid
            cycleId={cycle.id}
            cycleStatus={cycle.status}
            subject={gridQuery.data.subject}
            rows={gridQuery.data.rows}
            canWrite={canWrite}
            onSaved={() => {
              void gridQuery.refetch();
              onSaved();
            }}
          />
        )}
      </GatedSection>
    </section>
  );
}

// ---------- Visibility tab ----------

interface VisibilityTabProps {
  cycle: ExamCycleDetail;
  canManage: boolean;
  onChanged: () => void;
  audience: AudienceResponse | null;
}

interface VisibilityStudentRow {
  studentId: string;
  studentName: string;
  admissionNumber: string;
  isVisible: boolean;
  hasStudentOverride: boolean;
}

/**
 * Build a per-student resolver map from the cycle audience payload. The
 * audience endpoint is the single source of truth for `isVisible` and
 * `hasVisibilityOverride` so both this tab and the Audience tab agree on
 * what each student sees today.
 */
function buildVisibilityResolver(
  audience: AudienceResponse | null,
): Map<string, { isVisible: boolean; hasVisibilityOverride: boolean }> {
  const map = new Map<
    string,
    { isVisible: boolean; hasVisibilityOverride: boolean }
  >();
  if (!audience) return map;
  for (const group of audience.groupedByBatch) {
    for (const student of group.students) {
      map.set(student.studentId, {
        isVisible: student.isVisible,
        hasVisibilityOverride: student.hasVisibilityOverride,
      });
    }
  }
  return map;
}

function VisibilityTab({ cycle, canManage, onChanged, audience }: VisibilityTabProps) {
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const firstSubjectId = cycle.subjects[0]?.id ?? null;

  const studentsQuery = useQuery({
    queryKey: ['exams', 'visibility-students', cycle.id, firstSubjectId] as const,
    enabled: Boolean(firstSubjectId),
    queryFn: ({ signal }) => {
      // We piggy-back on the grid endpoint for the first subject to enumerate
      // active-enrolled students. The visibility-resolution endpoint does not
      // expose a per-student GET, so this is the cheapest way to populate a
      // roster while honouring the same scoping rules as the mark grid.
      if (!firstSubjectId) {
        return Promise.resolve({
          rows: [] as ReadonlyArray<{
            studentId: string;
            studentName: string;
            admissionNumber: string;
          }>,
        });
      }
      return apiRequest<{
        rows: ReadonlyArray<{
          studentId: string;
          studentName: string;
          admissionNumber: string;
        }>;
      }>(
        `/api/exams/cycles/${encodeURIComponent(
          cycle.id,
        )}/subjects/${encodeURIComponent(firstSubjectId)}/marks`,
        { signal },
      );
    },
  });

  const visibilityResolver = useMemo(
    () => buildVisibilityResolver(audience),
    [audience],
  );

  const studentRows: VisibilityStudentRow[] = useMemo(() => {
    if (!studentsQuery.data || !firstSubjectId) return [];
    return (studentsQuery.data.rows ?? []).map((r) => {
      // Prefer the per-student resolver from the audience endpoint. When
      // the audience response has not arrived yet (or omits this student),
      // fall back to the cycle-wide default and no-override state.
      const resolved = visibilityResolver.get(r.studentId);
      return {
        studentId: r.studentId,
        studentName: r.studentName,
        admissionNumber: r.admissionNumber,
        isVisible: resolved?.isVisible ?? cycle.visibility.cycleDefault,
        hasStudentOverride: resolved?.hasVisibilityOverride ?? false,
      };
    });
  }, [
    studentsQuery.data,
    firstSubjectId,
    cycle.visibility.cycleDefault,
    visibilityResolver,
  ]);

  const cycleVisibilityMutation = useMutation({
    mutationFn: (params: { isVisible: boolean; confirmIncompletePublish?: boolean }) =>
      apiRequest<{ id: string; isVisible: boolean }>(
        `/api/exams/cycles/${encodeURIComponent(cycle.id)}/visibility`,
        {
          method: 'POST',
          body: {
            isVisible: params.isVisible,
            ...(params.confirmIncompletePublish
              ? { confirmIncompletePublish: true }
              : {}),
          },
        },
      ),
    onSuccess: (res) => {
      toast(
        res.isVisible
          ? 'Marks are now visible to students and parents.'
          : 'Marks are now hidden from students and parents.',
        { variant: 'success' },
      );
      onChanged();
    },
    onError: async (err: unknown) => {
      // 422 marks_incomplete: ask the operator whether to publish anyway.
      // The server returns `{ subjectCount, studentCount, markCount,
      // expectedMarkCount, missing }` in details so we can show a useful
      // confirm prompt before re-submitting with the override flag.
      if (
        err instanceof ApiError &&
        err.status === 422 &&
        err.code === 'marks_incomplete'
      ) {
        const details = err.details as
          | {
              missing?: number;
              markCount?: number;
              expectedMarkCount?: number;
              studentCount?: number;
            }
          | undefined;
        const ok = await confirm({
          title: 'Some marks are missing',
          description: `${details?.markCount ?? 0} of ${details?.expectedMarkCount ?? 0} (subject × student) entries are recorded — ${details?.missing ?? 0} missing across ${details?.studentCount ?? 0} students. Publish anyway?`,
          confirmLabel: 'Publish anyway',
        });
        if (ok) {
          cycleVisibilityMutation.mutate({
            isVisible: true,
            confirmIncompletePublish: true,
          });
        }
        return;
      }
      toast(
        err instanceof ApiError ? err.message : 'Could not update visibility.',
        { variant: 'error' },
      );
    },
  });

  const studentOverrideMutation = useMutation({
    mutationFn: (params: { studentId: string; isVisible: boolean }) =>
      apiRequest<{ id: string; isVisible: boolean }>(
        `/api/exams/cycles/${encodeURIComponent(
          cycle.id,
        )}/visibility/students/${encodeURIComponent(params.studentId)}`,
        { method: 'POST', body: { isVisible: params.isVisible } },
      ),
    onSuccess: () => {
      toast('Override saved.', { variant: 'success' });
      onChanged();
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not save override.',
        { variant: 'error' },
      );
    },
  });

  return (
    <section className="space-y-5">
      <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold">
              Cycle-wide visibility
            </h3>
            <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
              When on, students and parents see marks for this cycle as soon
              as they're recorded. Per-student overrides below take precedence.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
                cycle.visibility.cycleDefault
                  ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
                  : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
              )}
            >
              {cycle.visibility.cycleDefault ? (
                <>
                  <Eye size={12} aria-hidden="true" />
                  Visible
                </>
              ) : (
                <>
                  <EyeOff size={12} aria-hidden="true" />
                  Hidden
                </>
              )}
            </span>
            {canManage ? (
              <Button
                size="sm"
                variant={cycle.visibility.cycleDefault ? 'secondary' : 'primary'}
                onClick={() =>
                  cycleVisibilityMutation.mutate({
                    isVisible: !cycle.visibility.cycleDefault,
                  })
                }
                loading={cycleVisibilityMutation.isPending}
                icon={
                  cycle.visibility.cycleDefault ? (
                    <EyeOff size={14} aria-hidden="true" />
                  ) : (
                    <Eye size={14} aria-hidden="true" />
                  )
                }
              >
                {cycle.visibility.cycleDefault ? 'Hide marks' : 'Publish marks'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
        <header className="border-b border-[color:var(--color-border)] px-5 py-3">
          <h3 className="font-display text-base font-semibold">
            Per-student overrides
          </h3>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Toggle a single student to differ from the cycle-wide default. The
            most-recent toggle wins; an audit entry is created each time.
          </p>
        </header>
        {studentsQuery.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : studentRows.length === 0 ? (
          <div className="p-5 text-sm text-[color:var(--color-text-muted)]">
            Add a subject and enroll students to manage per-student overrides.
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[color:var(--color-surface)]">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                  >
                    Student
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                  >
                    Resolved
                  </th>
                  {canManage ? (
                    <th
                      scope="col"
                      className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                    >
                      Action
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {studentRows.map((row) => (
                  <tr
                    key={row.studentId}
                    className="border-t border-[color:var(--color-border)]"
                  >
                    <td className="px-4 py-2 align-middle">
                      <div className="font-medium">{row.studentName}</div>
                      <div className="font-mono text-xs text-[color:var(--color-text-muted)]">
                        {row.admissionNumber}
                      </div>
                    </td>
                    <td className="px-4 py-2 align-middle">
                      <div className="inline-flex flex-wrap items-center gap-1.5">
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                            row.isVisible
                              ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
                              : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
                          )}
                        >
                          {row.isVisible ? 'Visible' : 'Hidden'}
                        </span>
                        {row.hasStudentOverride ? (
                          <span
                            className="inline-flex items-center rounded-full border border-gold-500/40 bg-gold-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold-700"
                            title="This student has a per-student visibility override."
                          >
                            Override
                          </span>
                        ) : null}
                      </div>
                    </td>
                    {canManage ? (
                      <td className="px-4 py-2 align-middle text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            studentOverrideMutation.mutate({
                              studentId: row.studentId,
                              isVisible: !row.isVisible,
                            })
                          }
                          disabled={studentOverrideMutation.isPending}
                        >
                          {row.isVisible ? 'Hide for this student' : 'Show for this student'}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ---------- Audience tab ----------

interface AudienceTabProps {
  audience: AudienceResponse | null;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}

function AudienceTab({ audience, loading, error, onRetry }: AudienceTabProps): JSX.Element {
  const errorState =
    error instanceof Error
      ? {
          message:
            error instanceof ApiError
              ? error.message
              : 'Could not load audience. Please try again.',
          onRetry,
        }
      : null;

  const totalStudents = audience?.totalStudents ?? 0;
  const groups = audience?.groupedByBatch ?? [];

  return (
    <RelationPanel
      title="Audience"
      description="Students grouped by batch, with per-subject recorded vs. missing counts and the per-student visibility resolved by the server."
      count={totalStudents}
      loading={loading}
      error={errorState}
      empty={{
        copy:
          'No students are enrolled in batches that this cycle scopes to. Enroll students into a batch to populate the audience.',
      }}
      isEmpty={!loading && !errorState && groups.length === 0}
    >
      <ul className="space-y-2">
        {groups.map((group) => (
          <AudienceBatch key={group.batchId} group={group} />
        ))}
      </ul>
    </RelationPanel>
  );
}

interface AudienceBatchProps {
  group: AudienceBatchGroup;
}

function AudienceBatch({ group }: AudienceBatchProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const studentCount = group.students.length;
  const recordedTotal = group.students.reduce(
    (sum, s) => sum + s.recordedSubjects,
    0,
  );
  const missingTotal = group.students.reduce(
    (sum, s) => sum + s.missingSubjects,
    0,
  );

  return (
    <li className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={clsx(
          'flex w-full items-center justify-between gap-3 px-4 py-3 text-left',
          'transition-colors hover:bg-[color:var(--color-surface)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <ChevronRight
              size={16}
              aria-hidden="true"
              className={clsx(
                'shrink-0 text-[color:var(--color-text-muted)] transition-transform',
                open && 'rotate-90',
              )}
            />
            <span className="truncate font-medium text-[color:var(--color-text)]">
              {group.batchName}
            </span>
          </div>
          {group.courseName ? (
            <p className="ml-6 mt-0.5 truncate text-xs text-[color:var(--color-text-muted)]">
              {group.courseName}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-xs tabular-nums text-[color:var(--color-text-muted)]">
          <span>
            {studentCount} student{studentCount === 1 ? '' : 's'}
          </span>
          <span aria-hidden="true">·</span>
          <span>
            <span className="text-[color:var(--color-success)]">{recordedTotal}</span>
            <span className="px-0.5">/</span>
            <span className="text-[color:var(--color-text)]">
              {recordedTotal + missingTotal}
            </span>{' '}
            recorded
          </span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={clsx(
              'shrink-0 transition-transform',
              open ? 'rotate-180' : 'rotate-0',
            )}
          />
        </div>
      </button>
      {open ? (
        <div className="border-t border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
          {studentCount === 0 ? (
            <p className="px-4 py-3 text-sm text-[color:var(--color-text-muted)]">
              No students enrolled in this batch yet.
            </p>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--color-surface)]">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                    >
                      Student
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                    >
                      Recorded
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                    >
                      Missing
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
                    >
                      Visibility
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.students.map((student) => (
                    <tr
                      key={student.studentId}
                      className="border-t border-[color:var(--color-border)]"
                    >
                      <td className="px-4 py-2 align-middle">
                        <div className="font-medium text-[color:var(--color-text)]">
                          {student.studentName}
                        </div>
                        <div className="font-mono text-xs text-[color:var(--color-text-muted)]">
                          {student.admissionNumber}
                        </div>
                      </td>
                      <td className="px-4 py-2 align-middle text-right tabular-nums">
                        {student.recordedSubjects}
                      </td>
                      <td
                        className={clsx(
                          'px-4 py-2 align-middle text-right tabular-nums',
                          student.missingSubjects > 0
                            ? 'text-[color:var(--color-danger)]'
                            : 'text-[color:var(--color-text-muted)]',
                        )}
                      >
                        {student.missingSubjects}
                      </td>
                      <td className="px-4 py-2 align-middle">
                        <AudienceVisibilityChip
                          isVisible={student.isVisible}
                          hasOverride={student.hasVisibilityOverride}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </li>
  );
}

interface AudienceVisibilityChipProps {
  isVisible: boolean;
  hasOverride: boolean;
}

function AudienceVisibilityChip({
  isVisible,
  hasOverride,
}: AudienceVisibilityChipProps): JSX.Element {
  const baseLabel = isVisible ? 'Parent visible' : 'Hidden';
  // When the student has an explicit per-student override, highlight the chip
  // (gold border + gold tint) and tack on a small "Override" badge so the
  // operator can tell at a glance which rows diverge from the cycle default.
  if (hasOverride) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span
          className={clsx(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
            'border-gold-500/60 bg-gold-50 text-gold-700',
          )}
        >
          {isVisible ? (
            <Eye size={12} aria-hidden="true" />
          ) : (
            <EyeOff size={12} aria-hidden="true" />
          )}
          {baseLabel}
        </span>
        <span className="inline-flex items-center rounded-full border border-gold-500/40 bg-gold-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold-700">
          Override
        </span>
      </span>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        isVisible
          ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
          : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
      )}
    >
      {isVisible ? (
        <Eye size={12} aria-hidden="true" />
      ) : (
        <EyeOff size={12} aria-hidden="true" />
      )}
      {baseLabel}
    </span>
  );
}

// ---------- Results tab ----------

function ResultsTab({ cycleId }: { cycleId: string }) {
  const resultsQuery = useQuery({
    queryKey: ['exams', 'cycle-results', cycleId] as const,
    queryFn: ({ signal }) =>
      apiRequest<CycleResults>(
        `/api/exams/cycles/${encodeURIComponent(cycleId)}/results`,
        { signal },
      ),
  });

  const handleExport = () => {
    if (!resultsQuery.data) return;
    const csv = buildResultsCsv(resultsQuery.data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${resultsQuery.data.cycle.name.replace(/\W+/g, '-').toLowerCase()}-results.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section className="space-y-4">
      <GatedSection
        error={resultsQuery.error}
        title="Results"
        onRetry={() => resultsQuery.refetch()}
      >
        {resultsQuery.isLoading || !resultsQuery.data ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold">
                Aggregate results
              </h3>
              <Button
                size="sm"
                variant="secondary"
                icon={<Download size={14} aria-hidden="true" />}
                onClick={handleExport}
                disabled={resultsQuery.data.perStudent.length === 0}
              >
                Export CSV
              </Button>
            </div>

            <ResultsPerSubject data={resultsQuery.data} />
            <ResultsPerStudent data={resultsQuery.data} />
          </>
        )}
      </GatedSection>
    </section>
  );
}

function ResultsPerSubject({ data }: { data: CycleResults }) {
  if (data.perSubject.length === 0) {
    return (
      <EmptyState
        title="No subjects to aggregate"
        description="Add subjects and record marks to populate the results view."
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <header className="border-b border-[color:var(--color-border)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Per-subject statistics
      </header>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <th scope="col" className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Subject
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Recorded
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Absent
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Passed
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Failed
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Avg
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                High
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Low
              </th>
            </tr>
          </thead>
          <tbody>
            {data.perSubject.map((row) => (
              <tr
                key={row.subjectId}
                className="border-t border-[color:var(--color-border)]"
              >
                <td className="px-4 py-2 align-middle">
                  <div className="font-medium">{row.subjectName}</div>
                  <div className="text-xs text-[color:var(--color-text-muted)]">
                    Max {row.maxMarks} · Pass {row.passMarks}
                  </div>
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums">
                  {row.recordedCount}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums">
                  {row.absentCount}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums text-[color:var(--color-success)]">
                  {row.passedCount}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums text-[color:var(--color-danger)]">
                  {row.failedCount}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums">
                  {row.averageMarks ?? '—'}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums">
                  {row.highestMarks ?? '—'}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums">
                  {row.lowestMarks ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultsPerStudent({ data }: { data: CycleResults }) {
  if (data.perStudent.length === 0) {
    return (
      <EmptyState
        title="No marks recorded yet"
        description="Per-student totals will appear here once marks are saved."
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <header className="border-b border-[color:var(--color-border)] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        Per-student totals
      </header>
      <div className="max-h-[60vh] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[color:var(--color-surface)]">
            <tr>
              <th scope="col" className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Student
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Attempted
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Passed
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Obtained
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Total
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                %
              </th>
              <th scope="col" className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Result
              </th>
            </tr>
          </thead>
          <tbody>
            {data.perStudent.map((row) => {
              const passed =
                row.subjectsAttempted > 0 &&
                row.subjectsPassed === row.subjectsAttempted;
              return (
                <tr
                  key={row.studentId}
                  className="border-t border-[color:var(--color-border)]"
                >
                  <td className="px-4 py-2 align-middle">
                    <div className="font-medium">{row.studentName}</div>
                    <div className="font-mono text-xs text-[color:var(--color-text-muted)]">
                      {row.admissionNumber}
                    </div>
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {row.subjectsAttempted}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {row.subjectsPassed}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {row.totalMarksObtained}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums text-[color:var(--color-text-muted)]">
                    {row.totalMaxMarks}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {row.averagePercent !== null ? `${row.averagePercent}%` : '—'}
                  </td>
                  <td className="px-4 py-2 align-middle text-right">
                    <span
                      className={clsx(
                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                        passed
                          ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
                          : 'border-[color:var(--color-danger)]/40 bg-[color:var(--color-danger)]/10 text-[color:var(--color-danger)]',
                      )}
                    >
                      {passed ? (
                        <>
                          <CheckCircle2 size={12} aria-hidden="true" />
                          Passed
                        </>
                      ) : (
                        <>
                          <ChevronRight size={12} aria-hidden="true" />
                          Needs review
                        </>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const csvEscape = (val: string | number | null): string => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const buildResultsCsv = (data: CycleResults): string => {
  const lines: string[] = [];
  lines.push(
    [
      'Student',
      'Admission #',
      'Subjects attempted',
      'Subjects passed',
      'Total obtained',
      'Total max',
      'Average %',
    ]
      .map(csvEscape)
      .join(','),
  );
  for (const row of data.perStudent) {
    lines.push(
      [
        row.studentName,
        row.admissionNumber,
        row.subjectsAttempted,
        row.subjectsPassed,
        row.totalMarksObtained,
        row.totalMaxMarks,
        row.averagePercent ?? '',
      ]
        .map(csvEscape)
        .join(','),
    );
  }
  return lines.join('\n');
};
